import { useCallback, useEffect, useRef } from 'react';

import * as Sentry from '@sentry/react';

import { RegistryOrderError } from '@/services/registry-order-error';
import { vnextApiClient } from '@/services/vnext-api';
import {
  isRegistryOrderInTransit,
  type RegistryOrder,
  type RegistryOrderCloseRequest,
  type RegistryOrderCreateRequest,
  type RegistryOrderDetail,
  type RegistryOrderList,
  type RegistryOrderOutcome,
  type RegistryOrderQuoteRecordRequest,
  type RegistryOrderReplyRequest,
} from '@/types/registry-documents';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll, withEtag } from './usePoll';

/** Orders move when someone prepares or discards one, at Start, and when a
 * delivery lands — minutes apart at the fastest. */
export const REGISTRY_ORDERS_POLL_MS = 30_000;

/** While a send is in the air the list is read every few seconds, so a row
 * leaves "Prepared" as the analyst watches rather than half a minute on. */
export const REGISTRY_ORDERS_TRANSIT_POLL_MS = 3_000;

/** How long the quick cadence lasts. A send still unresolved by then is
 * waiting on the retry sweep or on a person, which takes minutes. */
export const REGISTRY_ORDERS_TRANSIT_WINDOW_MS = 120_000;

const EMPTY: RegistryOrder[] = [];

interface UseRegistryOrdersOptions {
  enabled?: boolean;
  /** Whether the report has started, so prepared orders are being sent.
   * Null while the report has not loaded. */
  reportActivated?: boolean | null;
}

/**
 * A report's registry-document orders, polled with ETag revalidation, and
 * the actions on them.
 *
 * Every action re-reads the list afterwards, on failure too: an order call
 * that fails may still have left a row behind (a send that was refused
 * records a failed order; a timeout leaves one pending), and the typed
 * error's `orderMayExist` says so. The error is re-thrown for the caller
 * to show against what it was doing.
 *
 * Start sends the prepared orders in the background, after the screen has
 * its answer. So the list is re-read the moment the report is seen to
 * start, and then every few seconds while a send is in the air.
 */
export function useRegistryOrders(
  reportId: string | null,
  { enabled = true, reportActivated = null }: UseRegistryOrdersOptions = {}
) {
  const activated = reportActivated === true;
  // When the quick cadence began, and for which report: a hook that is
  // handed another report must not spend what is left of the last one's
  // window on it.
  const transitRef = useRef<{ reportId: string | null; since: number } | null>(null);

  const poll = usePoll<RegistryOrderList>({
    interval: (list) => {
      const inTransit =
        isFor(list, reportId) &&
        list.orders.some((order) => isRegistryOrderInTransit(order, activated));
      if (!inTransit) {
        transitRef.current = null;
        return REGISTRY_ORDERS_POLL_MS;
      }
      if (transitRef.current?.reportId !== reportId) {
        transitRef.current = { reportId, since: Date.now() };
      }
      return Date.now() - transitRef.current.since < REGISTRY_ORDERS_TRANSIT_WINDOW_MS
        ? REGISTRY_ORDERS_TRANSIT_POLL_MS
        : REGISTRY_ORDERS_POLL_MS;
    },
    enabled: enabled && reportId !== null,
    refetchDeps: [reportId],
    queryFn: async ({ signal, etag, manual }) => {
      if (!reportId) {
        return UNCHANGED;
      }
      const result = await vnextApiClient.listRegistryOrders(reportId, null, {
        signal,
        etag: manual ? null : etag,
      });
      return result === 'not-modified' ? UNCHANGED : withEtag(result.data, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });
  const { refetch } = poll;

  // The report started while this list was on screen: every prepared order
  // shown is now being sent, so the list is stale as of this render.
  const lastActivatedRef = useRef(reportActivated);
  useEffect(() => {
    const before = lastActivatedRef.current;
    lastActivatedRef.current = reportActivated;
    if (enabled && before === false && reportActivated === true) {
      // A fresh Start gets a fresh window, whatever unresolved send was
      // already on the report.
      transitRef.current = null;
      void refetch();
    }
  }, [enabled, reportActivated, refetch]);

  const settle = useCallback(
    async <T>(action: () => Promise<T>): Promise<T> => {
      try {
        const result = await action();
        await refetch();
        return result;
      } catch (err) {
        // A 409 on discard means the order was sent meanwhile, and on close
        // that a delivery or another person settled it; a 502/504 on a
        // send means a row changed state; a 404 means the row is gone.
        // Either way the screen is stale. Only a request refused outright
        // (validation, catalogue down) changed nothing.
        const unchanged =
          err instanceof RegistryOrderError && !err.orderMayExist && err.kind !== 'not_found';
        if (!unchanged) {
          await refetch();
        }
        throw err;
      }
    },
    [refetch]
  );

  /** Prepare an order (sent at Start, or at once on an activated report). */
  const create = useCallback(
    (payload: RegistryOrderCreateRequest): Promise<RegistryOrderDetail> =>
      settle(() => vnextApiClient.createRegistryOrder(payload)),
    [settle]
  );

  /** Discard a prepared order. */
  const discard = useCallback(
    (orderId: string): Promise<void> => settle(() => vnextApiClient.discardRegistryOrder(orderId)),
    [settle]
  );

  /** Send a prepared order now (activated reports only). */
  const place = useCallback(
    (orderId: string): Promise<RegistryOrderDetail> =>
      settle(() => vnextApiClient.placeRegistryOrder(orderId)),
    [settle]
  );

  /** Close a sent order that will never be completed. Nothing is sent to
   * Armadillo: the order is cancelled or declined with its desk first. */
  const close = useCallback(
    (orderId: string, request: RegistryOrderCloseRequest): Promise<RegistryOrderDetail> =>
      settle(() => vnextApiClient.closeRegistryOrder(orderId, request)),
    [settle]
  );

  /** Answer a message from the research desk. Recorded on the order, so
   * the list is re-read: the Reply badge clears, and a recorded quote
   * gives the order its expected date. */
  const reply = useCallback(
    (orderId: string, request: RegistryOrderReplyRequest): Promise<RegistryOrderDetail> =>
      settle(() => vnextApiClient.replyToRegistryOrder(orderId, request)),
    [settle]
  );

  /** Prepare one order per payload, in order, reporting each outcome as it
   * lands; a failure on one never stops the next. The list is re-read
   * once, at the end: every order placed changed it, and a failed one may
   * have left a row behind. */
  const createMany = useCallback(
    async (
      payloads: RegistryOrderCreateRequest[],
      onEach: (index: number, outcome: RegistryOrderOutcome) => void
    ): Promise<void> => {
      try {
        for (const [index, payload] of payloads.entries()) {
          try {
            // Sequential by design: Armadillo's desk takes one order at a
            // time, and the outcome of each is shown as it lands.
            const order = await vnextApiClient.createRegistryOrder(payload);
            onEach(index, { ok: true, order });
          } catch (err) {
            onEach(index, { ok: false, error: toError(err, 'Could not prepare the order') });
          }
        }
      } finally {
        await refetch();
      }
    },
    [refetch]
  );

  /** Record the desk's quote on its own, for one accepted outside the
   * Hub. The list is re-read: the row gains its price and its expected
   * date, and the Reply badge clears when the quote names the desk's
   * message. */
  const recordQuote = useCallback(
    async (
      orderId: string,
      request: RegistryOrderQuoteRecordRequest
    ): Promise<RegistryOrderDetail> => {
      try {
        return await settle(() => vnextApiClient.recordRegistryQuote(orderId, request));
      } catch (err) {
        // The expected date is never typed in: the quote is only offered
        // on a row without one, so a 422 for it means a colleague dated
        // the order after this row was read. The request changed nothing,
        // but the row that offered the quote is the stale one.
        if (err instanceof RegistryOrderError && err.code === 'expected_at_set') {
          await refetch();
        }
        throw err;
      }
    },
    [refetch, settle]
  );

  // The page can outlive a change of report, and the poll keeps its last
  // payload across a restart: a list is this report's only when it says so.
  const current = isFor(poll.data, reportId) ? poll.data : undefined;
  const awaitingOwnList = reportId !== null && current === undefined && poll.error === undefined;

  return {
    orders: current?.orders ?? EMPTY,
    isLoading: poll.isLoading || awaitingOwnList,
    error: poll.error === undefined ? null : toError(poll.error),
    refetch,
    create,
    createMany,
    discard,
    place,
    close,
    reply,
    recordQuote,
  };
}

function isFor(
  list: RegistryOrderList | undefined,
  reportId: string | null
): list is RegistryOrderList {
  return (
    list !== undefined &&
    reportId !== null &&
    list.reportId.toLowerCase() === reportId.toLowerCase()
  );
}
